import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "@picklebet/ui";
import { useHelpCenterStore } from "@/features/help-center/stores/helpCenterStore";
import { usePermission } from "@/features/auth/hooks/usePermission";
import { useWalletPermission } from "@/features/wallet/hooks/useWalletPermission";
import { CheckPermissions } from "@/features/wallet/components/CheckPermissions";
import { useIsProfileIomRegulatedMarketUnverified } from "@/hooks/firestore/user/useProfile";
import { useFiatCurrency } from "@/hooks/firestore/user/useFiatCurrency";
import {
  WithdrawWizard,
  WithdrawWizardSkeleton,
} from "../withdraw-wizard/WithdrawWizard";
import { WithdrawUnavailable } from "./WithdrawUnavailable";
import { WithdrawVerificationRequired } from "./WithdrawVerificationRequired";

import * as styles from "./WithdrawModal.module.scss";

type WithdrawModalProps = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
};

const WithdrawModal = ({ isOpen, onOpenChange }: WithdrawModalProps) => {
  // held above Modal.Root because an in-flight withdrawal has to seal the
  // modal's own dismiss routes, not just the wizard's inputs
  const [submitting, setSubmitting] = useState(false);

  const [verificationRequired, setVerificationRequired] = useState(false);
  const [withdrawUnavailable, setWithdrawUnavailable] = useState(false);

  const close = () => onOpenChange(false);
  const { permission: withdrawPermission, isLoading: isPermissionLoading } =
    useWalletPermission("withdraw");
  const canViewWithdrawModal = withdrawPermission !== "LOGIN_REQUIRED";
  const manageWalletPermission = usePermission("manageExternalWallet");
  const currency = useFiatCurrency();

  const {
    isProfileIomRegulatedMarketUnverified: isIomUnverified,
    isLoading: isIomLoading,
  } = useIsProfileIomRegulatedMarketUnverified();

  const isVerificationDeferred =
    isIomUnverified && withdrawPermission === "VERIFICATION_STATUS_UNSUPPORTED";

  const canWithdraw =
    withdrawPermission === "GRANTED" ||
    withdrawPermission === "CC_UNVERIFIED_MW_DEP";

  const canManageWallets = manageWalletPermission === "GRANTED";

  // deferred users reach the form and are stopped once they submit. Only
  // Interac wallets are managed in the wizard, so managing alone only lets CAD
  // users in. The currency is settled here because isIomLoading waits on the
  // profile.
  const canOpenWizard =
    canWithdraw ||
    isVerificationDeferred ||
    (currency === "CAD" && canManageWallets);

  // stable so the wizard's context value isn't rebuilt on every render here
  const showVerificationRequired = useCallback(
    () => setVerificationRequired(true),
    [],
  );

  const showWithdrawUnavailable = useCallback(
    () => setWithdrawUnavailable(true),
    [],
  );

  const isSupportOpen = useHelpCenterStore(
    (state) => state.visible || state.chatOpen,
  );

  useEffect(() => {
    if (isSupportOpen && isOpen) {
      close();
    }
  }, [isSupportOpen]);

  // reset the blocked screens when the modal closes
  useEffect(() => {
    if (!isOpen) {
      setVerificationRequired(false);
      setWithdrawUnavailable(false);
    }
  }, [isOpen]);

  const handleOpenChange = (next: boolean) => {
    if (next) {
      return;
    }

    close();
  };

  const renderBody = () => {
    // both resolve to the wizard in all but the blocked cases, so stand in at
    // its height. A spinner is ~140px shorter and jumps the modal on arrival.
    if (isPermissionLoading || isIomLoading) {
      return <WithdrawWizardSkeleton />;
    }

    const showWizard =
      !verificationRequired && !withdrawUnavailable && canOpenWizard;

    if (showWizard) {
      return (
        <WithdrawWizard
          close={close}
          submitting={submitting}
          setSubmitting={setSubmitting}
          mustVerifyToWithdraw={isIomUnverified}
          showVerificationRequired={showVerificationRequired}
          canWithdraw={canWithdraw}
          showWithdrawUnavailable={showWithdrawUnavailable}
          canManageWallets={canManageWallets}
        />
      );
    }

    return (
      <>
        <Modal.Header>
          <Modal.Heading />
        </Modal.Header>

        {verificationRequired ? (
          <WithdrawVerificationRequired />
        ) : (
          <CheckPermissions type="withdraw">
            <WithdrawUnavailable />
          </CheckPermissions>
        )}
      </>
    );
  };

  if (!canViewWithdrawModal) {
    return null;
  }

  return (
    <Modal.Root
      open={isOpen}
      onOpenChange={handleOpenChange}
      dismissible={!submitting}
    >
      <Modal.Body className={styles.modalBody}>
        <Modal.Title visuallyHidden>Withdraw funds</Modal.Title>
        <Modal.Subtitle visuallyHidden>
          Enter an amount to withdraw from your account
        </Modal.Subtitle>

        {renderBody()}
      </Modal.Body>
    </Modal.Root>
  );
};

export { WithdrawModal };
export type { WithdrawModalProps };
